import React, { useRef, useState } from 'react';
import { useT } from '../../../i18n/LanguageProvider';
import { engagementService } from '../../../services/request/engagement';

interface Props {
    cardId: string;
    recipientEmail: string;
    readOnly?: boolean;
}

const CommentSection: React.FC<Props> = ({ cardId, recipientEmail, readOnly }) => {
    const { t } = useT();
    const [name, setName] = useState('');
    const [text, setText] = useState('');
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const textRef = useRef<HTMLTextAreaElement>(null);

    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (!text.trim()) return setError('Please write something before submitting.');
        if (!isAnonymous && !name.trim()) return setError('Please enter your name, or choose anonymous.');
        setSubmitting(true);
        try {
            await engagementService.addComment(cardId, {
                authorEmail: recipientEmail,
                authorName: name.trim() || 'Anonymous',
                commentText: text.trim(),
                isAnonymous,
            });
            setText('');
            setName('');
            setSubmitted(true);
        } catch {
            setError('Failed to post comment. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mt-4 px-5 py-4 bg-white bg-opacity-85 backdrop-blur-sm rounded-2xl shadow-md w-full max-w-lg">
            <p className="text-xs font-semibold text-slate-500 mb-3 tracking-widest uppercase">{t('viewcard.comments')}</p>

            {submitted ? (
                <p className="text-sm text-green-600 mb-3">Thank you for your comment!</p>
            ) : (
                <>
                <p className="text-sm text-slate-400 mb-3">{t('viewcard.leaveComment')}</p>

            {/* Comment form */}
            <form onSubmit={handleSubmit} className={`flex flex-col gap-2 ${readOnly ? 'opacity-50 pointer-events-none' : ''}`}>
                {!isAnonymous && (
                    <input
                        type="text"
                        placeholder={t('viewcard.yourName')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={80}
                        className="text-sm px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 outline-none focus:border-indigo-400"
                    />
                )}
                <textarea
                    ref={textRef}
                    placeholder={t('viewcard.writeMessage')}
                    value={text}
                    onChange={(e) => setText(e.target.value.slice(0, 500))}
                    rows={3}
                    className="text-sm px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 outline-none resize-none focus:border-indigo-400 leading-relaxed"
                />
                <div className="flex items-center gap-2.5">
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isAnonymous}
                            onChange={(e) => setIsAnonymous(e.target.checked)}
                            className="mr-1.5"
                        />
                        {t('viewcard.postAnonymously')}
                    </label>
                    <span className="text-xs text-slate-400 ml-auto">{text.length}/500</span>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="px-4 py-1.5 bg-indigo rounded-lg text-white text-xs font-semibold hover:opacity-80 transition-opacity duration-150 disabled:opacity-50"
                    >
                        {submitting ? `${t('viewcard.post')}…` : t('viewcard.post')}
                    </button>
                </div>
                {error && <p className="text-xs text-red-500 m-0">{error}</p>}
            </form>
                </>
            )}
        </div>
    );
};

export default CommentSection;
